import { useOutletContext, useSearchParams } from 'react-router-dom'

import {
  Box,
  Button,
  Card,
  EnhancedDimension,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { PerformanceReviewOverviewPendingEvaluationsTaskDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { MyReviews } from 'modules/performance_process/components/overview/myReviews'
import { ReviewOverviewOutletContext } from 'modules/performance_process/pages/reviews/show/overview'

interface Props {
  minWidth?: EnhancedDimension
}

export const PendingEvaluationsTask = ({ minWidth }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSidepanelOpen = searchParams.get('myReviewsPanel') === 'open'
  const context = useOutletContext<ReviewOverviewOutletContext>()
  const { access } = useCurrentSession()

  const { data, loading } = useQuery(PerformanceReviewOverviewPendingEvaluationsTaskDocument, {
    variables: {
      id: context.reviewProcess.id,
      currentAccessId: access.id,
    },
  })

  const reviewProcess = data?.performance.reviewProcess

  if (loading || !reviewProcess) return null

  const allEvaluationsCount = reviewProcess.myParticipation?.evaluationsCount ?? 0
  const publishedEvaluationsCount = reviewProcess.myParticipation?.publishedEvaluationsCount ?? 0
  const pendingEvaluationsCount = allEvaluationsCount - publishedEvaluationsCount

  const isCompleted = pendingEvaluationsCount === 0 || reviewProcess.status === 'finished'

  const onClick = () => setSearchParams({ myReviewsPanel: 'open' })

  if (allEvaluationsCount === 0) return null

  return (
    <Box minWidth={minWidth} width={minWidth}>
      <Card onClick={onClick}>
        <Card.Title
          size="small"
          action={
            !isCompleted && (
              <Tag leftItem="circle" inline capitalize type="tangerine">
                {i18n.t('performance_process.overview.tasks.pending_tasks', {
                  count: pendingEvaluationsCount,
                })}
              </Tag>
            )
          }
        >
          {i18n.t('performance_process.overview.tasks.reviews.title')}
        </Card.Title>
        <Card.Content>
          <Box gap="s16" width="full" flexGrow justifyContent="spaceBetween">
            <Text.Normal color="grey1000">
              {i18n.t('performance_process.overview.tasks.reviews.description')}
            </Text.Normal>
            <Button onClick={onClick} type="secondary">
              {isCompleted
                ? i18n.t('performance_process.overview.tasks.view_all')
                : i18n.t('performance_process.overview.tasks.reviews.action')}
            </Button>
          </Box>
        </Card.Content>
      </Card>

      <Sidepanel
        open={isSidepanelOpen}
        onClose={() => setSearchParams({})}
        width="wideXL"
        title={i18n.t('performance_process.reviews.completed_reviews')}
      >
        <MyReviews />
      </Sidepanel>
    </Box>
  )
}
